import { useEffect, useCallback, useRef } from "react";

import { useAppDispatch, useAppSelector } from "../store";
import {
  selectSubscriptionPlans,
  selectCurrentPlan,
  selectUserLimits,
  selectUserFeatures,
  selectHasActiveSubscription,
  selectHasAllVoices,
  selectSubscriptionLoading,
  selectSubscriptionError,
  selectSubscriptionLastFetchedAt,
  fetchSubscriptionSnapshot,
} from "../store/slices/subscriptionSlice";

import type {
  SubscriptionPlan,
  UserSubscription,
  UserLimits,
} from "../types/subscription";

interface UseSubscriptionReturn {
  loading: boolean;
  error: string | null;
  plans: SubscriptionPlan[];
  currentPlan: UserSubscription | null;
  userLimits: UserLimits | null;
  userFeatures: Record<string, boolean | number | string> | null;
  hasActiveSubscription: boolean;
  hasAllVoices: boolean;
  lastFetchedAt: number;
  refetch: (force?: boolean) => Promise<void>;
}

// Хук извлекает данные из Redux + делает ленивую / кэшируемую загрузку snapshot
export const useSubscription = (): UseSubscriptionReturn => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector(selectSubscriptionLoading);
  const error = useAppSelector(selectSubscriptionError);
  const plans = useAppSelector(selectSubscriptionPlans) as SubscriptionPlan[];
  const currentPlan = useAppSelector(
    selectCurrentPlan,
  ) as UserSubscription | null;
  const userLimits = useAppSelector(selectUserLimits) as UserLimits | null;
  const userFeatures = useAppSelector(selectUserFeatures) as Record<
    string,
    boolean | number | string
  > | null;
  const hasActiveSubscription = useAppSelector(selectHasActiveSubscription);
  const hasAllVoices = useAppSelector(selectHasAllVoices);
  const lastFetchedAt = useAppSelector(selectSubscriptionLastFetchedAt);

  // ФЛАГ: предотвращаем повторные запуски
  const fetchInitiatedRef = useRef(false);

  // Инициируем загрузку если ещё не загружали
  useEffect(() => {
    if (!lastFetchedAt && !loading && !fetchInitiatedRef.current) {
      fetchInitiatedRef.current = true; // Помечаем что загрузка началась
      dispatch(fetchSubscriptionSnapshot(undefined));
    }
  }, [dispatch, lastFetchedAt, loading]);

  const refetch = useCallback(
    async (force?: boolean) => {
      await dispatch(fetchSubscriptionSnapshot(Boolean(force)))
        .unwrap()
        .catch(() => {});
    },
    [dispatch],
  );

  return {
    loading,
    error,
    plans,
    currentPlan,
    userLimits,
    userFeatures: userFeatures || null,
    hasActiveSubscription,
    hasAllVoices,
    lastFetchedAt,
    refetch,
  };
};
